import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useAuth,
  useI18N,
  usePostAddClaims,
  VenueDetailsBySlugResponse,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { Textarea } from "@/shadcn/components/ui/textarea";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import ModalWithAction from "@/components/common/ModalWithAction";
import { trackEvent, setUserProperties } from "common/analytics";

function AddClaim(props: {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onClose: (isSuccess?: boolean) => void;
  onInfoClick: () => void;
  flagVenueAccessibilityId: string | null;
  onRefresh?: () => void;
}) {
  const i18n = useI18N();
  const { user, isLoading: isUserDataLoading, isLoggedIn } = useAuth();
  const { modalStyle, modalClassName } = useModalHeight();

  const showToast = useCustomToast();

  const [description, setDescription] = useState("");
  const { data: mutateResult, error, status, mutate } = usePostAddClaims(true); // Add true to prevent automatic invalidation
  const noteRef = useRef(null);
  const isDark = useDarkMode();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Zoom awareness state and logic
  const isZoomedIn = useZoomDetection();

  // Claim form (page `claim_business_form`) — a shared widget opened from the
  // venue-details claim CTA and the flag-report "this is my business" path, so
  // its events use the trackEvent primitive (no single page scope).
  // Guard the mount fire so it lands exactly once per open: React StrictMode
  // double-invokes effects in dev, which otherwise double-counts the event.
  // AddClaim is conditionally rendered (unmounts on close), so a fresh open is a
  // fresh mount with a fresh ref — no reset needed.
  const openTrackedRef = useRef(false);
  useEffect(() => {
    if (openTrackedRef.current) return;
    openTrackedRef.current = true;
    trackEvent("claim_business_form_opened", {
      brand_id: props.venueDetailsResponse.details?.brandId ?? "",
      brand_name: props.venueDetailsResponse.details?.brandName ?? "",
      business_id:
        props.venueDetailsResponse.details?.business?.id?.toString() ?? "",
      place_name: props.venueDetailsResponse.details?.name ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isUserDataLoading) {
      if (user) {
        setName(user?.name || "");
        setEmail(user?.email || "");
      }
    }
  }, [isUserDataLoading, user]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (mutateResult) {
      console.log("mutateResult", mutateResult);
      if (mutateResult.status === "success") {
        // Refresh the claimed_business_* people properties. Claims are immediate and the response
        // returns the full updated list (nested under `data`); Mixpanel people.set replaces, so set
        // the whole list rather than appending.
        if (Array.isArray(mutateResult.data?.claimed_businesses)) {
          setUserProperties({
            claimed_business_id: mutateResult.data.claimed_businesses.map(
              (b: { business_id: number | string }) => String(b.business_id),
            ),
            claimed_business_name: mutateResult.data.claimed_businesses.map(
              (b: { business_name: string }) => b.business_name,
            ),
          });
        }

        showToast(mutateResult.message, "success");
        setName("");
        setEmail("");
        setPhone("");
        setDescription("");

        // Call onRefresh to update venue details if provided
        if (props.onRefresh) {
          console.log("Calling onRefresh after successful claim submission");
          setTimeout(() => {
            props.onRefresh();
          }, 1000);
        }

        props.onClose(true); // Pass true for successful submission
      } else {
        showToast(mutateResult.message, "error");
        setName("");
        setEmail("");
        setPhone("");
        setDescription("");
        props.onClose(false); // Pass false for failed submission
      }
    }
  }, [mutateResult]);

  const submit = useCallback(() => {
    // Claim submitted — business_claimed. contact_method reflects which
    // channel(s) were provided (email is required, phone optional).
    trackEvent("business_claimed", {
      brand_id: props.venueDetailsResponse.details?.brandId ?? "",
      brand_name: props.venueDetailsResponse.details?.brandName ?? "",
      business_id:
        props.venueDetailsResponse.details?.business?.id?.toString() ?? "",
      place_name: props.venueDetailsResponse.details?.name ?? "",
      contact_method: phone ? (email ? "both" : "phone") : "email",
    });

    console.log(
      "props.flagVenueAccessibilityId",
      props.flagVenueAccessibilityId,
    );

    // Store the venue ID in localStorage if user is not logged in
    if (!isLoggedIn) {
      try {
        const venueId =
          props.venueDetailsResponse.details.business.id.toString();
        const storedClaimsStr = localStorage.getItem("claimed_venues") || "[]";
        const storedClaims = JSON.parse(storedClaimsStr);

        if (!storedClaims.includes(venueId)) {
          storedClaims.push(venueId);
          localStorage.setItem("claimed_venues", JSON.stringify(storedClaims));
        }
      } catch (e) {
        console.error("Error updating claimed venues in localStorage", e);
      }
    }

    mutate({
      venueId:
        props.venueDetailsResponse.details?.business?.id.toString() || "",
      name: name,
      email: email,
      phone: phone,
      description: description,
      flagVenueAccessibilityId: props.flagVenueAccessibilityId || "",
    });
  }, [
    props.venueDetailsResponse.details,
    props.flagVenueAccessibilityId,
    user?.name,
    isLoggedIn,
    mutate,
    name,
    email,
    phone,
    description,
  ]);

  const isSubmitDisabled = () => {
    return name === "" || email === "";
  };

  const handleCloseAttempt = () => {
    // Check if user has made any changes
    const hasChanges =
      name !== (user?.name || "") ||
      email !== (user?.email || "") ||
      phone !== "" ||
      description !== "";

    if (hasChanges) {
      setShowConfirmModal(true);
    } else {
      props.onClose();
    }
  };

  const handleConfirmLeave = () => {
    setShowConfirmModal(false);
    props.onClose();
  };

  const handleStay = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      <section
        style={modalStyle}
        className={cn(modalClassName)}
        role="dialog"
        aria-labelledby="modal-title"
      >
        {status === "pending" && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"}`}
        >
          <StandardButton
            onClick={handleCloseAttempt}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <h1
            className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
            tabIndex={0}
            id="modal-title"
          >
            {i18n.get("claimMyBusiness")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>{" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto py-3"
          role="region"
        >
          <div className="self-stretch px-6 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold">
                {i18n.get("claimMyBusinessText")}
              </div>

              <div className="self-stretch py-2 inline-flex flex-col justify-start items-start gap-2.5 w-full">
                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
              </div>

              <div className="self-stretch min-w-11 inline-flex flex-col justify-center items-center">
                <div className="self-stretch !text-fg-neutral-subtle label-m-medium">
                  Business name
                </div>
                <div className="self-stretch !text-fg-brand-subtle title-s-semibold">
                  {props.venueDetailsResponse.details.name}
                </div>
              </div>

              <div className="self-stretch py-2 inline-flex flex-col justify-start items-start gap-2.5 w-full">
                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
              </div>
            </div>
            <div className="self-stretch rounded-lg justify-start items-center gap-8">
              <div className="flex flex-col mt-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <FloatingLabelInput
                      label={i18n.get("contactName")}
                      required
                      type="text"
                      value={name}
                      placeholder={i18n.get("contactName")}
                      aria-label={i18n.get("contactName")}
                      onChange={(event) => {
                        setName(event.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="self-stretch justify-center">
              <div className="self-stretch justify-center">
                <span className="text-fg-neutral-strong title-s-semibold">
                  {i18n.get("howCanWeContactYou")}
                </span>
                <span className="text-red-600 title-s-semibold">*</span>
              </div>
              <div className="self-stretch justify-center text-fg-brand-subtle body-l-semibold">
                {i18n.get("emailIsRequired")}
              </div>
            </div>

            <div className="self-stretch rounded-lg justify-start items-center gap-8">
              <div className="flex flex-col mt-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <FloatingLabelInput
                      label={i18n.get("contactEmail")}
                      required
                      type="email"
                      value={email}
                      placeholder={i18n.get("contactEmail")}
                      aria-label={i18n.get("contactEmail")}
                      onChange={(event) => {
                        setEmail(event.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="self-stretch rounded-lg justify-start items-center gap-8">
              <div className="flex flex-col mt-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <FloatingLabelInput
                      label={i18n.get("contactPhone")}
                      type="text"
                      value={phone}
                      placeholder={i18n.get("contactPhone")}
                      aria-label={i18n.get("contactPhone")}
                      onChange={(event) => {
                        setPhone(event.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full">
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsMoreAboutVenue")}
                  className="scrollbar-thin h-28"
                  value={description}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                />
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky bottom-0 z-10" : ""}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Submit claim"
              >
                {i18n.get("submit")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>

      <ModalWithAction
        isModalOpen={showConfirmModal}
        title={i18n.get("areYouSureYouWantToLeaveClaimProcess")}
        subtitle={i18n.get("yourClaimDetailsWillBeLost")}
        cancelButtonTitle={i18n.get("stay")}
        actionButtonTitle={i18n.get("leave")}
        onClose={handleStay}
        onClick={handleConfirmLeave}
        type="warning"
        actionButtonType="error"
      />
    </>
  );
}

export default AddClaim;
